import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment";
import axiosInstance from "../../../services/AxiosInstance";
import Api from "../../../constants/Api";
import PageTitle from "../../layouts/PageTitle";
import LoadingCenter from "../../components/Common/LoadingCenter";
import { ApiError } from "../../components/Common/ApiError";
import ReportExportButtons from "../../components/Common/Report/ReportExportButtons";
import useCompanyBranding from "../../components/Common/Report/useCompanyBranding";
import EmployeeSelect from "../../components/Custom/Main/Employee/EmployeeSelect";
import { useBranch } from "../../../context/BranchContext";
import { convertDoubleToStringPTBR } from "../../../utils/ConverterUtils";
import { buildWhatsappUrl } from "../../../utils/FormatUtils";
import '../../components/Common/Report/ReportFilters.css';
import './PotReport.css';

/** O pote é apurado por mês fechado, então os atalhos partem daí. */
const QUICK_PERIODS = {
    this_month: () => [moment().startOf('month'), moment().endOf('month')],
    last_month: () => [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
};

const toApiDate = (date) => date.format('YYYY-MM-DD');
const formatCurrency = (value) => `R$ ${convertDoubleToStringPTBR(Number(value || 0))}`;
const formatPercentage = (value) => `${convertDoubleToStringPTBR(Number(value || 0))}%`;
const formatInteger = (value) => Number(value || 0).toLocaleString('pt-BR');

/**
 * Estilos que vão junto para a janela de impressão, que é um documento novo e não herda o
 * CSS da aplicação. Sem eles o PDF sai como texto corrido, sem cards, sem faixas de filial
 * e sem as bordas das tabelas.
 */
const PRINT_STYLES = `
    @page { size: A4 portrait; margin: 12mm 10mm; }
    /* O Chrome salva PDF com "gráficos de segundo plano" desmarcado por padrão, o que
       apagaria os cards, o cabeçalho das tabelas e a faixa de cada filial. */
    html, body, * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    body { font-size: 11px; }
    .pot-report__header { margin-bottom: 14px; padding-bottom: 10px; border-bottom: 2px solid #d32f2f; text-align: center; }
    .pot-report__header h1 { margin: 0 0 4px; font-size: 19px; }
    .pot-report__header h2 { margin: 0 0 4px; font-size: 15px; }
    .pot-report__header p { margin: 2px 0 0; color: #5f6368; font-size: 11px; }
    .pot-report__adjusted { color: #d32f2f !important; font-weight: 600; }
    /* Cards do resumo em quatro colunas: cabem na largura do A4 sem apertar o número. */
    .pot-report__summary { display: flex; flex-wrap: wrap; gap: 8px; margin: 14px 0 18px; }
    .pot-report__card { flex: 1 1 22%; padding: 8px 10px; background: #f3f4f6; border: 1px solid #d8dde3; border-radius: 6px; page-break-inside: avoid; }
    .pot-report__card--accent { border-left: 3px solid #d32f2f; }
    .pot-report__card-label { display: block; color: #5f6368; font-size: 8.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
    .pot-report__card-value { display: block; margin-top: 2px; font-size: 13px; font-weight: 700; }
    .pot-report__card-hint { display: block; color: #6b7280; font-size: 9px; }
    .pot-report__filter-note { margin: 10px 0 0; padding: 6px 10px; background: #f3f4f6; border-left: 3px solid #d32f2f; font-size: 10px; color: #5f6368; }
    .pot-report__branch { margin-top: 18px; page-break-inside: avoid; }
    .pot-report__branch-title { margin: 0 0 6px; padding: 6px 10px; background: #f3f4f6; border-left: 3px solid #d32f2f; font-size: 13px; }
    .pot-report__branch-summary { display: flex; flex-wrap: wrap; gap: 4px 16px; margin-bottom: 8px; color: #5f6368; font-size: 10px; }
    /* A rolagem é recurso de tela: no papel a tabela precisa sair inteira. */
    .pot-report__table-scroll { max-height: none; overflow: visible; border: 0; }
    .pot-report__table { width: 100%; min-width: 0; margin-top: 0; border-collapse: collapse; font-size: 10px; }
    .pot-report__table th, .pot-report__table td { padding: 4px 6px; border: 1px solid #cfcfcf; }
    .pot-report__table thead th { position: static; background: #eef1f4; font-size: 9px; text-transform: uppercase; }
    .pot-report__cell--emphasis { font-weight: 700; }
    .pot-report__adjusted-badge { display: block; color: #d32f2f; font-size: 9px; }
    /* O detalhe por serviço sai sempre aberto: é como o profissional confere as fichas.
       O recuo e o fundo mais claro marcam que ele pertence à linha de cima, senão as duas
       tabelas se confundem em uma só. */
    .pot-report__services-row--print-only { display: table-row; }
    .pot-report__services-row > td { padding: 4px 6px 8px 18px; background: #fbfcfd; }
    .pot-report__services { width: 100%; margin: 4px 0; font-size: 9.5px; }
    .pot-report__services th, .pot-report__services td { padding: 3px 5px; border: 1px solid #dfe3e8; }
    .pot-report__services th { background: #f7f8fa; font-size: 8.5px; text-transform: uppercase; }
`;

/**
 * Relatório do Pote: como a parcela das assinaturas foi repartida no período, da empresa
 * até o serviço prestado por cada profissional.
 *
 * Substitui o documento que as barbearias recebiam do sistema anterior. O rateio da filial
 * segue as FICHAS, e não a receita de assinaturas que ela vendeu: é o que faz a soma dos
 * profissionais bater com o total da filial, e o das filiais com o da empresa.
 */
const PotReport = () => {

    const { t } = useTranslation();

    // A filial ativa vem do cabeçalho, como nas demais telas. Sem filial escolhida, o
    // relatório consolida a empresa e abre uma seção por filial.
    const { selectedBranch } = useBranch();
    const branchId = selectedBranch?.id ?? selectedBranch?.value ?? null;

    const [period, setPeriod] = useState(() => {
        const [start, end] = QUICK_PERIODS.this_month();
        return { startDate: toApiDate(start), endDate: toApiDate(end) };
    });
    const [report, setReport] = useState(null);
    // Filtro só de exibição: o pote é rateado entre a empresa toda, então escolher um
    // profissional não pode recalcular nada. Ele some as linhas dos outros; os cards e os
    // totais de cada filial continuam sendo os do rateio completo, que é o que fecha.
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [loading, setLoading] = useState(false);
    // Profissional aberto: o detalhe por serviço é longo e o gestor confere um por vez.
    const [openEmployeeId, setOpenEmployeeId] = useState(null);
    const contentRef = useRef(null);
    const branding = useCompanyBranding();

    const invalidPeriod = moment(period.endDate).isBefore(moment(period.startDate));

    const load = useCallback(() => {
        if (invalidPeriod) {
            return;
        }

        setLoading(true);
        axiosInstance.get(Api.reports.pot, { params: { ...period, branchId } })
            .then(({ data }) => setReport(data))
            .catch((error) => { setReport(null); ApiError(error); })
            .finally(() => setLoading(false));
    }, [period, branchId, invalidPeriod]);

    useEffect(() => { load(); }, [load]);

    const applyQuickPeriod = (key) => {
        const [start, end] = QUICK_PERIODS[key]();
        setPeriod({ startDate: toApiDate(start), endDate: toApiDate(end) });
    };

    const isQuickPeriodActive = (key) => {
        const [start, end] = QUICK_PERIODS[key]();
        return period.startDate === toApiDate(start) && period.endDate === toApiDate(end);
    };

    const periodLabel = `${moment(period.startDate).format('DD/MM/YYYY')} a ${moment(period.endDate).format('DD/MM/YYYY')}`;
    const employeeId = selectedEmployee?.id ?? selectedEmployee?.value ?? null;

    // Filial sem o profissional escolhido sai da lista: manter a seção vazia só faria o
    // gestor procurar uma linha que não existe ali.
    const allBranches = report?.branches || [];
    const branches = employeeId == null ? allBranches : allBranches
        .map(branch => ({
            ...branch,
            employees: (branch.employees || []).filter(item => item.employeeId === employeeId)
        }))
        .filter(branch => branch.employees.length > 0);

    // Uma linha por serviço de cada profissional: é o nível que o gestor confere quando
    // questiona um valor, e o que a planilha precisa ter para ser recalculável.
    const exportRows = branches.flatMap(branch => branch.employees.flatMap(employee =>
        (employee.services || []).map(service => [
            branch.branchName,
            employee.employeeName,
            service.productName,
            formatInteger(service.score),
            formatInteger(service.quantity),
            formatCurrency(employee.value),
            formatPercentage(employee.percentage),
        ])));

    const exportPreamble = report ? [
        ['Relatório do Pote'],
        ['Período', periodLabel],
        ['Total das assinaturas', formatCurrency(report.subscriptionTotal)],
        ['Percentual do pote', formatPercentage(report.potPercentage)],
        ['Valor do pote', formatCurrency(report.potValue)],
        ['Pago aos profissionais', formatCurrency(report.employeesValue)],
        // O adicional é pago pela empresa, então aparece entre o pote e o líquido dela: é o
        // que explica a diferença entre os dois números na planilha.
        ...(Number(report.bonusTotal || 0) > 0
            ? [['Adicionais combinados', formatCurrency(report.bonusTotal)]]
            : []),
        ['Comissão líquida da empresa', formatCurrency(report.companyValue)],
        [],
    ] : [];

    const buildWhatsAppMessage = (branch, employee) => {
        const linhas = (employee.services || [])
            .map(service => `• ${service.productName}: ${formatInteger(service.quantity)} atendimento(s), ${formatInteger(service.score)} fichas`)
            .join('\n');
        return `*Pote ${periodLabel}*\n${employee.employeeName} - ${branch.branchName}\n\n`
            + `${linhas}\n\nTotal: ${formatInteger(employee.totalScore)} fichas\n`
            + `Sua parte: *${formatCurrency(employee.value)}* (${formatPercentage(employee.percentage)} do pote)`;
    };

    const summaryCards = report ? [
        { label: 'Serviços realizados', value: formatInteger(report.serviceCount) },
        { label: 'Total de fichas', value: formatInteger(report.totalScore) },
        { label: 'Total das assinaturas', value: formatCurrency(report.subscriptionTotal) },
        { label: `Pote (${formatPercentage(report.potPercentage)})`, value: formatCurrency(report.potValue), accent: true },
        { label: 'Pago aos profissionais', value: formatCurrency(report.employeesValue), hint: formatPercentage(report.employeesPercentage) },
        // Só existe quando alguém tem acordo. Zerado, seria mais um card para o gestor ler
        // sem que ele diga nada sobre o mês.
        ...(Number(report.bonusTotal || 0) > 0
            ? [{ label: 'Adicionais combinados', value: formatCurrency(report.bonusTotal), hint: 'pagos pela empresa' }]
            : []),
        { label: 'Líquido da empresa', value: formatCurrency(report.companyValue), hint: formatPercentage(report.companyPercentage) },
    ] : [];

    return (
        <Fragment>
            <PageTitle activeMenu={t('pot_report')} motherMenu={t('reports')} />

            <div className="report-filters">
                <Row className="g-3 align-items-end">
                    <Col xl={3} md={6}>
                        <Form.Group>
                            <Form.Label>{t('employee')}</Form.Label>
                            {/* filterProvidesService desligado: quem entra no rateio é quem
                                tem ficha no período, e não só quem está marcado como
                                prestador de serviço no cadastro. */}
                            <EmployeeSelect
                                branch={selectedBranch}
                                value={selectedEmployee}
                                onChange={(option) => setSelectedEmployee(option)}
                                filterProvidesService={false}
                            />
                        </Form.Group>
                    </Col>
                    <Col xl={3} md={6}>
                        <Form.Group>
                            <Form.Label>{t('date_start')}</Form.Label>
                            <Form.Control
                                type="date"
                                value={period.startDate}
                                onChange={(event) => setPeriod(current => ({ ...current, startDate: event.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    <Col xl={3} md={6}>
                        <Form.Group>
                            <Form.Label>{t('date_end')}</Form.Label>
                            <Form.Control
                                type="date"
                                value={period.endDate}
                                onChange={(event) => setPeriod(current => ({ ...current, endDate: event.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    {/* Atalhos abaixo dos campos, ocupando a linha: mesmo desenho do extrato
                        do colaborador, para o filtro não mudar de forma a cada relatório. */}
                    <Col xs={12}>
                        <div className="report-filters__quick-periods">
                            {Object.keys(QUICK_PERIODS).map(key => (
                                <Button
                                    key={key}
                                    size="sm"
                                    variant={isQuickPeriodActive(key) ? 'primary' : 'outline-primary'}
                                    onClick={() => applyQuickPeriod(key)}
                                >
                                    {t(`period_shortcut.${key}`)}
                                </Button>
                            ))}
                        </div>
                    </Col>
                </Row>

                {invalidPeriod && (
                    <div className="report-filters__warning">
                        <i className="fas fa-triangle-exclamation me-2"></i>
                        {t('validation.period_start_after_end')}
                    </div>
                )}
            </div>

            {loading ? <LoadingCenter /> : !report || branches.length === 0 ? (
                <div className="card"><div className="card-body">
                    <p className="report-empty">
                        {employeeId != null
                            ? 'Este profissional não tem pontuação no período selecionado.'
                            : 'Nenhuma pontuação encontrada no período selecionado.'}
                    </p>
                </div></div>
            ) : (
                <div className="card">
                    <div className="card-body">
                        <div className="pot-report__actions d-print-none">
                            <ReportExportButtons
                                contentRef={contentRef}
                                headers={['Filial', 'Profissional', 'Serviço', 'Fichas', 'Quantidade', 'Comissão', '% do pote']}
                                rows={exportRows}
                                preambleRows={exportPreamble}
                                fileName={`pote_${period.startDate}_a_${period.endDate}`}
                                documentTitle={`Relatório do Pote - ${periodLabel}`}
                                extraStyles={PRINT_STYLES}
                            />
                        </div>

                        <div ref={contentRef} className="pot-report">
                            <div className="pot-report__header">
                                {/* Logo com estilo inline: o PDF é montado a partir do
                                    innerHTML, e o que está inline vai junto mesmo que
                                    nenhuma folha de estilo chegue na janela nova. */}
                                {branding.logo && (
                                    <img
                                        className="pot-report__logo"
                                        src={branding.logo}
                                        alt={branding.name || 'Logo da empresa'}
                                        style={{ display: 'block', maxWidth: '160px', maxHeight: '70px', margin: '0 auto 8px', objectFit: 'contain' }}
                                    />
                                )}
                                {branding.name && <h2>{branding.name}</h2>}
                                <h1>Relatório do Pote</h1>
                                <p>{periodLabel}</p>
                                {report.potValueOriginal != null && (
                                    <p className="pot-report__adjusted">
                                        Valor ajustado manualmente. Cálculo original: {formatCurrency(report.potValueOriginal)}
                                    </p>
                                )}
                            </div>

                            {employeeId != null && (
                                <p className="pot-report__filter-note">
                                    Mostrando apenas {selectedEmployee?.label || 'o profissional selecionado'}.
                                    Os totais acima continuam sendo os da empresa no período, porque o pote é
                                    rateado entre todos.
                                </p>
                            )}

                            <div className="pot-report__summary">
                                {summaryCards.map(card => (
                                    <div key={card.label} className={`pot-report__card${card.accent ? ' pot-report__card--accent' : ''}`}>
                                        <span className="pot-report__card-label">{card.label}</span>
                                        <strong className="pot-report__card-value">{card.value}</strong>
                                        {card.hint && <span className="pot-report__card-hint">{card.hint} do total</span>}
                                    </div>
                                ))}
                            </div>

                            {branches.map(branch => (
                                <section key={branch.branchId} className="pot-report__branch">
                                    <h2 className="pot-report__branch-title">{branch.branchName}</h2>
                                    <div className="pot-report__branch-summary">
                                        <span><b>{formatInteger(branch.serviceCount)}</b> serviços</span>
                                        <span><b>{formatInteger(branch.totalScore)}</b> fichas</span>
                                        <span>Bruto da filial: <b>{formatCurrency(branch.grossValue)}</b> ({formatPercentage(branch.grossPercentage)})</span>
                                        <span>Pago aos profissionais: <b>{formatCurrency(branch.employeesValue)}</b> ({formatPercentage(branch.employeesPercentage)})</span>
                                        <span>Líquido da filial: <b>{formatCurrency(branch.netValue)}</b> ({formatPercentage(branch.netPercentage)})</span>
                                    </div>

                                    <div className="pot-report__table-scroll">
                                        <table className="pot-report__table">
                                            <thead>
                                                <tr>
                                                    <th>Profissional</th>
                                                    <th className="text-end">Fichas</th>
                                                    <th className="text-end">Serviços</th>
                                                    <th className="text-end">% do pote</th>
                                                    <th className="text-end">Comissão</th>
                                                    <th className="d-print-none"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {branch.employees.map(employee => (
                                                    <Fragment key={employee.employeeId}>
                                                        <tr
                                                            className="pot-report__employee-row"
                                                            onClick={() => setOpenEmployeeId(current =>
                                                                current === employee.employeeId ? null : employee.employeeId)}
                                                        >
                                                            <td>
                                                                {employee.employeeName}
                                                                {employee.originalScore != null && (
                                                                    <span className="pot-report__adjusted-badge">
                                                                        pontuação ajustada (era {formatInteger(employee.originalScore)})
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="text-end">{formatInteger(employee.totalScore)}</td>
                                                            <td className="text-end">{formatInteger(employee.serviceCount)}</td>
                                                            <td className="text-end">{formatPercentage(employee.percentage)}</td>
                                                            <td className="text-end pot-report__cell--emphasis">
                                                                {formatCurrency(employee.value)}
                                                                {/* A comissão dele já vem com o adicional somado. Sem
                                                                    esta linha, o valor não fecha com o percentual de
                                                                    fichas ao lado e parece erro de cálculo. */}
                                                                {Number(employee.bonusValue || 0) > 0 && (
                                                                    <small className="d-block text-muted">
                                                                        inclui {formatCurrency(employee.bonusValue)} de adicional
                                                                        ({formatPercentage(employee.bonusPercent)})
                                                                    </small>
                                                                )}
                                                            </td>
                                                            <td className="text-end d-print-none">
                                                                <i className={`fa-solid ${openEmployeeId === employee.employeeId ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                                            </td>
                                                        </tr>

                                                        {/* Na impressão o detalhe sai sempre: o documento entregue ao
                                                            profissional precisa mostrar de onde vieram as fichas dele. */}
                                                        <tr className={openEmployeeId === employee.employeeId
                                                            ? 'pot-report__services-row'
                                                            : 'pot-report__services-row pot-report__services-row--print-only'}>
                                                            <td colSpan={6}>
                                                                <table className="pot-report__services">
                                                                    <thead>
                                                                        <tr><th>Serviço</th><th className="text-end">Fichas</th><th className="text-end">Quantidade</th></tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(employee.services || []).map(service => (
                                                                            <tr key={service.productName}>
                                                                                <td>{service.productName}</td>
                                                                                <td className="text-end">{formatInteger(service.score)}</td>
                                                                                <td className="text-end">{formatInteger(service.quantity)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                    <tfoot>
                                                                        <tr>
                                                                            <td><b>Total</b></td>
                                                                            <td className="text-end"><b>{formatInteger(employee.totalScore)}</b></td>
                                                                            <td className="text-end"><b>{formatInteger(employee.serviceCount)}</b></td>
                                                                        </tr>
                                                                    </tfoot>
                                                                </table>

                                                                {employee.phone && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="success"
                                                                        className="pot-report__whatsapp d-print-none"
                                                                        onClick={() => window.open(
                                                                            buildWhatsappUrl(employee.phone, buildWhatsAppMessage(branch, employee)), '_blank')}
                                                                    >
                                                                        <i className="fab fa-whatsapp me-1"></i>
                                                                        Enviar para o profissional
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    </Fragment>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default PotReport;
